import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScoreBar, NumberInput, VASSlider } from "./shared";

function ASDASBands() {
  return [
    { label: "Inactive", max: 1.29, colour: "text-emerald-700" },
    { label: "Moderate", max: 2.09, colour: "text-teal-600" },
    { label: "High",     max: 3.49, colour: "text-amber-600" },
    { label: "Very High",max: 40,   colour: "text-red-600" },
  ];
}

export function ASDASCRPCalc() {
  const [back, setBack]       = useState(0);
  const [ms, setMs]           = useState(0);
  const [global, setGlobal]   = useState(0);
  const [periph, setPeriph]   = useState(0);
  const [crp, setCrp]         = useState(5);

  const score =
    0.121 * back +
    0.058 * ms +
    0.110 * global +
    0.073 * periph +
    0.579 * Math.log(crp + 1);

  const bands = ASDASBands();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">ASDAS-CRP</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">axSpA / AS</Badge>
            <Badge variant="outline">BSR / ASAS</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500 font-mono">0.121×back + 0.058×MS + 0.110×global + 0.073×periph + 0.579×ln(CRP+1)</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <VASSlider label="Back pain (NRS 0–10)" value={back} onChange={setBack} hint="Average from last week" />
          <VASSlider label="Duration morning stiffness (NRS 0–10)" value={ms} onChange={setMs} hint="0=none, 10=≥2 hours" />
          <VASSlider label="Patient global assessment (NRS 0–10)" value={global} onChange={setGlobal} />
          <VASSlider label="Peripheral pain/swelling (NRS 0–10)" value={periph} onChange={setPeriph} />
          <NumberInput label="CRP (mg/L)" value={crp} onChange={setCrp} min={0} step={0.1} hint="If CRP normal, use 2–5 mg/L" />
        </div>
        <ScoreBar score={score} bands={bands} />
        <div className="grid grid-cols-4 gap-1 text-center text-xs mt-1">
          {bands.map((b) => (
            <div key={b.label} className={`p-1.5 rounded bg-slate-50 border border-slate-100 ${b.colour}`}>
              <p className="font-semibold">{b.label}</p>
              <p className="text-slate-400">{b.label === "Inactive" ? "<1.3" : b.label === "Moderate" ? "1.3–2.1" : b.label === "High" ? "2.1–3.5" : ">3.5"}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400">MCID = 1.1 · PASS (patient acceptable symptom state) = ≤2.1</p>
      </CardContent>
    </Card>
  );
}

export function ASDASESRCalc() {
  const [back, setBack]       = useState(0);
  const [ms, setMs]           = useState(0);
  const [global, setGlobal]   = useState(0);
  const [periph, setPeriph]   = useState(0);
  const [esr, setEsr]         = useState(10);

  const score =
    0.113 * back +
    0.293 * Math.sqrt(Math.max(1, esr)) +
    0.086 * periph +
    0.069 * ms +
    0.079 * global;

  const bands = ASDASBands();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">ASDAS-ESR</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">axSpA / AS</Badge>
            <Badge variant="outline">BSR / ASAS</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500 font-mono">0.113×back + 0.293×√ESR + 0.086×periph + 0.069×MS + 0.079×global</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <VASSlider label="Back pain (NRS 0–10)" value={back} onChange={setBack} />
          <VASSlider label="Duration morning stiffness (NRS 0–10)" value={ms} onChange={setMs} />
          <VASSlider label="Patient global assessment (NRS 0–10)" value={global} onChange={setGlobal} />
          <VASSlider label="Peripheral pain/swelling (NRS 0–10)" value={periph} onChange={setPeriph} />
          <NumberInput label="ESR (mm/hr) — min 1" value={esr} onChange={(v) => setEsr(Math.max(1, v))} min={1} />
        </div>
        <ScoreBar score={score} bands={bands} />
        <p className="text-xs text-slate-400">ASDAS-CRP preferred if CRP available — more validated for treatment decisions</p>
      </CardContent>
    </Card>
  );
}
